/**
 * Every conversation in the current project — the page behind the sidebar's
 * "View all".
 *
 * The sidebar shows the newest few; a project can hold a hundred and more, and a
 * narrow column is no place to scroll or search that many. Here they have the
 * full width: grouped by day (today, yesterday, the last week, earlier), with a
 * search over the titles.
 *
 * Search and grouping are done on the client because the server gives no other
 * choice: `GET /threads` returns the whole list in one answer with no paging,
 * and a thread carries a title and a `created_at` and nothing else. That also
 * means there is nothing more to fetch — the list on this page is the same one
 * the sidebar reads, lifted into the shell (`useThreads`).
 *
 * A conversation that is running or waiting on an approval says so on its row,
 * with the same marks the sidebar's pinned rows use, so the two never disagree
 * about what is live.
 */
import { useMemo, useState, useSyncExternalStore } from "react";
import { useLocale } from "../i18n/LocaleProvider";
import type { ThreadsState } from "../hooks/useThreads";
import type { ChatSessions } from "../lib/chatSessions";
import { filterThreads, groupThreadsByDay, parseThreadTime, type ThreadDayGroup } from "../lib/sidebarChats";
import type { Thread } from "../lib/threads";
import { LoadingBlock, Spinner } from "../components/settings/Spinner";

function SearchIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.3-4.3" />
    </svg>
  );
}

const GROUP_LABEL_KEYS: Record<ThreadDayGroup, string> = {
  today: "chats.group.today",
  yesterday: "chats.group.yesterday",
  week: "chats.group.week",
  earlier: "chats.group.earlier",
};

/** The time for a recent row, the date for an older one — what tells neighbours apart in each group. */
function formatWhen(thread: Thread, group: ThreadDayGroup, locale: string): string | null {
  const at = parseThreadTime(thread.created_at);
  if (!at) return null;
  const tag = locale === "zh" ? "zh-TW" : "en-US";
  return group === "today" || group === "yesterday"
    ? at.toLocaleTimeString(tag, { hour: "2-digit", minute: "2-digit" })
    : at.toLocaleDateString(tag, { month: "short", day: "numeric" });
}

export default function ChatsPane({
  threads: state,
  projectName,
  activeThreadId,
  sessions,
  onOpen,
  onRetry,
}: {
  /** The current project's conversations, newest first; `idle` when no project is selected. */
  threads: ThreadsState;
  projectName: string | null;
  /** The conversation the chat has open, marked in the list. */
  activeThreadId: string | null;
  /** Every conversation's live state — which rows are running or waiting. */
  sessions: ChatSessions;
  onOpen: (threadId: string) => void;
  onRetry: () => void;
}) {
  const { t, locale } = useLocale();
  const [search, setSearch] = useState("");
  useSyncExternalStore(sessions.subscribe, () => sessions.version);
  const live = new Map(sessions.active().map((session) => [session.threadId!, session]));

  const all = state.kind === "loaded" ? state.threads : [];
  const query = search.trim();
  const groups = useMemo(() => groupThreadsByDay(filterThreads(all, query), new Date()), [all, query]);

  return (
    <section data-pane="chats">
      <div className="pane-head">
        <div className="pane-head-title">
          <h2>{t("chats.title")}</h2>
          {state.kind === "loading" ? <Spinner /> : null}
          {state.kind === "loaded" ? (
            <span className="pane-head-count">
              {projectName ? `${projectName} · ` : ""}
              {t("chats.count", { count: all.length })}
            </span>
          ) : null}
        </div>
      </div>

      {state.kind === "loaded" ? (
        <div className="search-field projects-search">
          <span className="search-field-icon">
            <SearchIcon />
          </span>
          <input
            className="settings-input"
            placeholder={t("chats.searchPlaceholder")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      ) : null}

      {state.kind === "idle" ? (
        <div className="settings-card">
          <p className="settings-note">{t("projects.noneSelected")}</p>
        </div>
      ) : state.kind === "loading" ? (
        <LoadingBlock size={40} label={t("chat.loading")} />
      ) : state.kind === "access-lost" ? (
        <div className="settings-card">
          <p className="settings-note">{t("chat.projectAccessLost")}</p>
        </div>
      ) : state.kind === "error" ? (
        <div className="settings-card">
          <p className="settings-msg err" role="alert">
            {state.message}
          </p>
          <button type="button" className="settings-btn secondary" onClick={onRetry}>
            {t("projects.retry")}
          </button>
        </div>
      ) : groups.length === 0 ? (
        <div className="settings-card is-muted">
          <p className="settings-note is-centered">{query ? t("chats.noMatches") : t("chat.noThreads")}</p>
        </div>
      ) : (
        groups.map(({ group, threads }) => (
          <div className="chats-group" key={group}>
            <div className="chats-group-head">{t(GROUP_LABEL_KEYS[group])}</div>
            <div className="settings-card chats-card">
              <ul className="chats-list">
                {threads.map((thread) => {
                  const session = live.get(thread.thread_id);
                  const waiting = session ? !session.state.busy && session.state.interrupt !== null : false;
                  const when = formatWhen(thread, group, locale);
                  const current = thread.thread_id === activeThreadId;
                  return (
                    <li key={thread.thread_id}>
                      <button
                        type="button"
                        className={`chats-row${current ? " is-active" : ""}`}
                        aria-current={current ? "true" : undefined}
                        onClick={() => onOpen(thread.thread_id)}
                      >
                        <span className="chats-row-title">{thread.title || t("chat.newConversation")}</span>
                        {session ? (
                          <span className={`chats-row-live${waiting ? " is-waiting" : ""}`}>
                            {waiting ? (
                              <span className="approval-pulse" aria-hidden="true">
                                <i />
                                <i />
                              </span>
                            ) : (
                              <Spinner size={14} />
                            )}
                            {waiting ? t("shell.running.needsApproval") : t("shell.running.working")}
                          </span>
                        ) : null}
                        {when ? <span className="chats-row-when">{when}</span> : null}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        ))
      )}
    </section>
  );
}
